import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Loader2 } from 'lucide-react';
import { Dialog, Button, ICON_SIZE } from '../design-system';
import { getPlatformLabel } from '../utils/pwaUtils';
import { forceUpdate, forceCleanAndRelogin } from '../utils/pwaUtils';
import { getLocalVersion, getRemoteVersionInfo, compareVersions } from '../services/versionService';
import { platform } from '../lib/native';
import {
  getAndroidInstallSource,
  openExternal,
  getUpdateRoute,
  openPlayListing,
  SITE_DOWNLOAD_URL,
} from '../lib/androidUpdateRoute';
import './VersionModal.css';

const APP_STORE_URL = 'https://apps.apple.com/app/reedle-ai-reading-ask-pdf/id6757764461';
const APK_URL = import.meta.env.VITE_APK_URL || 'https://fhgxapmrciwlhsffdeyj.supabase.co/storage/v1/object/public/app-releases/reedle-android-latest.apk';

/**
 * VersionModal — shows platform, local/remote version, status, and update actions.
 *
 * Opened manually from the sidebar footer menu.
 * No auto-popup (architecture supports adding it later).
 *
 * P4-b: `forcedInfo` ({ latest, min, force_update, release_notes }) renders a
 * blocking dialog (no close, no clean button) for below-min clients. Pass null
 * for the normal manually-opened modal.
 */
const VersionModal = ({ isOpen, onClose, forcedInfo = null }) => {
  const { t } = useTranslation(['sidebar']);

  const [remoteInfo, setRemoteInfo] = useState(null); // { latest, min, force_update, release_notes }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null); // 'update' | 'clean' | null
  // Android only: 'play' | 'sideload' | 'unknown' (null until the native query returns)
  const [installSource, setInstallSource] = useState(null);

  const localVersion = getLocalVersion();
  const platformLabel = getPlatformLabel();

  // Fetch remote version when modal opens (skipped in forced mode: info comes in)
  useEffect(() => {
    if (!isOpen || forcedInfo) return;
    let cancelled = false;

    // 打开时重置状态并拉取远端版本（不在 effect 体内同步 setState）
    const resetAndFetch = async () => {
      setLoading(true);
      setError(false);
      setActionInProgress(null);

      const info = await getRemoteVersionInfo('reedle');
      if (cancelled) return;
      if (info) {
        setRemoteInfo(info);
      } else {
        setError(true);
      }
      setLoading(false);
    };

    resetAndFetch();

    return () => { cancelled = true; };
  }, [isOpen]);

  // Android: who installed us decides the update route (Play listing vs APK)
  useEffect(() => {
    if (!isOpen || !platform.isAndroid) return;
    let cancelled = false;
    getAndroidInstallSource().then((src) => {
      if (!cancelled) setInstallSource(src);
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  const androidRoute = platform.isAndroid && installSource ? getUpdateRoute(installSource) : null;

  // P4-fix: declared before first use — line 65 read it before the old
  // declaration site below, a TDZ ReferenceError that crashed /settings.
  const isDisabled = actionInProgress !== null;

  // Compute status - guard against malformed remoteInfo
  // In forced mode the caller already decided: always 'forced'.
  const remote = forcedInfo || remoteInfo;
  const closable = !forcedInfo && !isDisabled;
  let status = 'ok'; // 'ok' | 'update' | 'forced'
  if (forcedInfo) {
    status = 'forced';
  } else if (remote && remote.latest) {
    const cmp = compareVersions(localVersion, remote.latest);
    if (cmp < 0) {
      status = remote.force_update ? 'forced' : 'update';
    }
    // Also check if below minimum
    if (remote.min && compareVersions(localVersion, remote.min) < 0) {
      status = 'forced';
    }
  }

  const statusLabel =
    status === 'forced'
      ? t('sidebar:version.updateRequired')
      : status === 'update'
        ? t('sidebar:version.updateAvailable')
        : t('sidebar:version.upToDate');

  const handleUpdate = async () => {
    if (actionInProgress) return;
    if (platform.isIOS) {
      await openExternal(APP_STORE_URL);
      return;
    }
    if (platform.isAndroid) {
      if (!androidRoute) return;
      if (androidRoute.primary === 'play') {
        await openPlayListing();
      } else {
        await openExternal(APK_URL);
      }
      return;
    }
    setActionInProgress('update');
    // forceUpdate() ends with window.location.reload() — page navigates away
    await forceUpdate();
  };

  const handleOpenSite = async () => {
    await openExternal(SITE_DOWNLOAD_URL);
  };

  const handleForceClean = async () => {
    if (actionInProgress) return;
    if (!confirm(t('sidebar:confirm.forceClean'))) return;
    setActionInProgress('clean');
    // forceCleanAndRelogin() ends with window.location.href = '/login' — page navigates away
    await forceCleanAndRelogin();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closable ? onClose : undefined}
      title={
        <span className="version-modal-title">
          <Info size={ICON_SIZE.md} />
          {t('sidebar:version.title')}
        </span>
      }
      size="sm"
      closeOnBackdrop={closable}
      closeOnEscape={closable}
      showClose={closable}
      footer={
        <>
          {!forcedInfo && (
          <Button variant="danger" onClick={handleForceClean} disabled={isDisabled}>
            {actionInProgress === 'clean' && <Loader2 size={ICON_SIZE.xs} className="version-spinner" />}
            {t('sidebar:version.forceClean')}
          </Button>
          )}
          <Button variant="primary" onClick={handleUpdate} disabled={isDisabled || (platform.isAndroid && !androidRoute)}>
            {actionInProgress === 'update' && <Loader2 size={ICON_SIZE.xs} className="version-spinner" />}
            {platform.isIOS
              ? t('sidebar:version.openAppStore')
              : platform.isAndroid
                ? !androidRoute
                  ? t('sidebar:version.loading')
                  : androidRoute.primary === 'apk'
                    ? t('sidebar:version.downloadApk')
                    : t('sidebar:version.openGooglePlay')
                : t('sidebar:version.update')}
          </Button>
        </>
      }
    >
      <div className="version-modal-content">
        <div className="version-info-row">
          <span className="version-info-label">{t('sidebar:version.platform')}</span>
          <span className="version-info-value">{platformLabel}</span>
        </div>

        <div className="version-info-row">
          <span className="version-info-label">{t('sidebar:version.localVersion')}</span>
          <span className="version-info-value">{localVersion}</span>
        </div>

        <div className="version-info-row">
          <span className="version-info-label">{t('sidebar:version.remoteVersion')}</span>
          <span className="version-info-value">
            {loading && !forcedInfo
              ? t('sidebar:version.loading')
              : error && !forcedInfo
                ? t('sidebar:version.unavailable')
                : remote?.latest || '-'}
          </span>
        </div>

        {!loading && !error && remote && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span className={`version-status version-status--${status}`}>
              {statusLabel}
            </span>
          </div>
        )}

        {androidRoute?.showSiteLink && (
          <button type="button" className="version-site-link" onClick={handleOpenSite}>
            {t('sidebar:version.playUnavailable')}
          </button>
        )}

        {(!loading || forcedInfo) && remote?.release_notes && (
          <>
            <span className="version-info-label" style={{ marginTop: 4 }}>
              {t('sidebar:version.releaseNotes')}
            </span>
            <div className="version-release-notes">
              {remote.release_notes}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default VersionModal;
